import type { Membership } from 'types/membership';

/** Same labels as Add/Edit membership type dropdown (for list filtering). */
export const MEMBERSHIP_TYPE_FILTER_OPTIONS = [
  'Pastor / Minister',
  'Board / Council (Administration)',
  'Elder / Leader',
  'Member (at least 1 year of attendance)',
  'Attender (less than 1 year of attendance)',
] as const;

/** Map stored values to current dropdown label for filter matching. */
export function mapMembershipTypeToDropdownValue(
  raw: string | null | undefined,
): string {
  const v = raw?.trim() ?? '';
  if (!v) return '';

  switch (v) {
    case 'Pastors/Ministers':
      return 'Pastor / Minister';
    case 'Administrators (Secretary, Finance Team...)':
      return 'Board / Council (Administration)';
    case 'Elder/Leader':
      return 'Elder / Leader';
    case 'Member (at least 1 year attendance)':
      return 'Member (at least 1 year of attendance)';
    case 'Attender (less than a year attendance)':
      return 'Attender (less than 1 year of attendance)';
    default:
      return v;
  }
}

export type MembershipGenderFilterValue = '' | 'male' | 'female' | 'other';
export type MembershipGenderChoice = Exclude<MembershipGenderFilterValue, ''>;
export type YesNoChoice = 'yes' | 'no';
type MultiValue<T extends string> = T | T[];

function normalizeFilterValues<T extends string>(value: MultiValue<T>): T[] {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
}

export function membershipPassesFieldFilters(
  m: Membership,
  filters: {
    gender?: MultiValue<MembershipGenderChoice>;
    department?: MultiValue<string>;
    civilStatus?: MultiValue<string>;
    occupation?: MultiValue<string>;
    nationality?: MultiValue<string>;
    isSgl?: MultiValue<YesNoChoice>;
    isSgm?: MultiValue<YesNoChoice>;
    willingToWorkInTupad?: MultiValue<YesNoChoice>;
    membershipType: MultiValue<string>;
  },
): boolean {
  const genderFilters = normalizeFilterValues(filters.gender ?? []);
  const departmentFilters = normalizeFilterValues(filters.department ?? []);
  const civilStatusFilters = normalizeFilterValues(filters.civilStatus ?? []);
  const occupationFilters = normalizeFilterValues(filters.occupation ?? []);
  const nationalityFilters = normalizeFilterValues(filters.nationality ?? []);
  const isSglFilters = normalizeFilterValues(filters.isSgl ?? []);
  const isSgmFilters = normalizeFilterValues(filters.isSgm ?? []);
  const willingFilters = normalizeFilterValues(
    filters.willingToWorkInTupad ?? [],
  );
  const membershipTypeFilters = normalizeFilterValues(filters.membershipType);

  if (
    genderFilters.length > 0 &&
    !genderFilters.includes(
      (m.gender?.toLowerCase() ?? '') as MembershipGenderChoice,
    )
  ) {
    return false;
  }

  if (
    departmentFilters.length > 0 &&
    !departmentFilters.includes(m.departmentMinistry?.trim() ?? '')
  ) {
    return false;
  }

  if (
    civilStatusFilters.length > 0 &&
    !civilStatusFilters.includes(m.civilStatus?.trim() ?? '')
  ) {
    return false;
  }

  if (
    occupationFilters.length > 0 &&
    !occupationFilters.includes(m.occupation?.trim() ?? '')
  ) {
    return false;
  }

  if (
    nationalityFilters.length > 0 &&
    !nationalityFilters.includes(m.nationality?.trim() ?? '')
  ) {
    return false;
  }

  if (isSglFilters.length > 0) {
    const value: YesNoChoice = m.isSgl ? 'yes' : 'no';
    if (!isSglFilters.includes(value)) {
      return false;
    }
  }

  if (isSgmFilters.length > 0) {
    const value: YesNoChoice = m.isSgm ? 'yes' : 'no';
    if (!isSgmFilters.includes(value)) {
      return false;
    }
  }

  if (willingFilters.length > 0) {
    const value: YesNoChoice = m.willingToWorkInTupad ? 'yes' : 'no';
    if (!willingFilters.includes(value)) {
      return false;
    }
  }

  if (membershipTypeFilters.length > 0) {
    const normalized = mapMembershipTypeToDropdownValue(m.membershipType);
    if (!membershipTypeFilters.includes(normalized)) {
      return false;
    }
  }

  return true;
}
